import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  IoChevronBack,
  IoInformationCircleOutline,
  IoCallOutline,
} from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { requestTossPayment, buildOrderId } from '../../../services/tossPaymentService';

// 더미 결제 항목
const DUMMY_ITEM = {
  id: 'pr1',
  totalAmount: 60000,
  status: 'pending',
  items: [
    { id: 'i1', label: '배정 결제', amount: 60000 },
  ],
};

const fmt = (n) => `₩${(n || 0).toLocaleString()}`;

const formatPhone = (val) => {
  const d = val.replace(/[^0-9]/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const PaymentProcess = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userData } = useAuth();
  const [item, setItem] = useState(DUMMY_ITEM);
  const amount = item.totalAmount;
  const [depositorName, setDepositorName] = useState('');
  const [depositorPhone, setDepositorPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id || id === 'pr1') return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'partner_payment_requests', id));
        if (snap.exists()) {
          const d = snap.data();
          setItem({
            id: snap.id,
            totalAmount: d.amount || d.totalAmount || 0,
            status: d.status || 'pending',
            items: d.items || [{ id: 'i1', label: d.label || '본사 청구', amount: d.amount || 0 }],
          });
        }
      } catch (err) { console.error('load payment request failed', err); }
    })();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!depositorName.trim()) return alert('입금자명을 입력해 주세요.');
    if (!depositorPhone.trim()) return alert('입금자 연락처를 입력해 주세요.');
    if (!amount || amount < 100) return alert('결제 금액이 올바르지 않습니다.');

    setSubmitting(true);
    const orderId = buildOrderId('pr');
    localStorage.setItem(`order:${orderId}`, JSON.stringify({
      kind: 'payment_request',
      paymentRequestId: id,
      partnerUid: userData?.uid,
      depositorName,
      depositorPhone,
      amount,
    }));
    try {
      await requestTossPayment({
        amount,
        orderId,
        orderName: `본사 청구 결제 #${id}`,
        customerName: depositorName,
        method: '카드',
      });
    } catch (err) {
      localStorage.removeItem(`order:${orderId}`);
      alert(err?.message || '결제창 호출 실패');
      setSubmitting(false);
    }
  };

  return (
    <Wrap>
      <BackRow>
        <BackBtn type="button" onClick={() => navigate(-1)}>
          <IoChevronBack size={20} />
          뒤로
        </BackBtn>
      </BackRow>

      <PageTitleWrap>
        <PageTitle>결제하기</PageTitle>
      </PageTitleWrap>

      <NoticeBox>
        <IoInformationCircleOutline size={14} />
        결제 요청 ID: {id}
      </NoticeBox>

      {/* 결제 요청 정보 카드 */}
      <BigAmountCard>
        <BigAmount>{fmt(item.totalAmount)}</BigAmount>
        <StatusBadge>정산 대기</StatusBadge>
      </BigAmountCard>

      {/* 결제 항목 리스트 */}
      <Card>
        <CardHeader>결제 항목</CardHeader>
        {item.items.map((it) => (
          <ItemRow key={it.id}>
            <ItemLabel>{it.label}</ItemLabel>
            <ItemAmount>{fmt(it.amount)}</ItemAmount>
          </ItemRow>
        ))}
      </Card>

      <NoticeBoxLong>
        <IoInformationCircleOutline size={14} />
        <NoticeContent>
          <strong>결제 안내</strong><br />
          입금자명·연락처를 입력하고 결제 버튼을 누르면 토스 결제창이 열립니다.
          결제 완료 시 본 청구는 자동으로 결제 완료 처리됩니다.
        </NoticeContent>
      </NoticeBoxLong>

      <Divider />

      <PageTitle>결제자 정보</PageTitle>
      <PageDesc>결제자 이름과 연락처를 입력해 주세요.</PageDesc>

      <form onSubmit={handleSubmit}>
        <Field>
          <Label>금액</Label>
          <Input
            type="number"
            value={amount}
            readOnly
          />
        </Field>

        <Field>
          <Label>결제자 이름</Label>
          <Input
            type="text"
            placeholder="결제자 이름"
            value={depositorName}
            onChange={(e) => setDepositorName(e.target.value)}
          />
        </Field>

        <Field>
          <Label>연락처</Label>
          <InputWrap>
            <InputIcon>
              <IoCallOutline size={16} />
            </InputIcon>
            <InputWithIcon
              type="tel"
              placeholder="010-1234-5678"
              value={depositorPhone}
              onChange={(e) => setDepositorPhone(formatPhone(e.target.value))}
            />
          </InputWrap>
        </Field>

        <NoticeBoxSmall>
          <IoInformationCircleOutline size={12} />
          결제창 결제 완료 시 자동 처리됩니다. 결제가 실패하면 다시 시도해 주세요.
        </NoticeBoxSmall>

        <SubmitBtn type="submit" disabled={submitting}>
          {submitting ? '결제창 여는 중...' : '결제하기'}
        </SubmitBtn>
      </form>
    </Wrap>
  );
};

export default PaymentProcess;

// ───── 스타일 ─────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    max-width: 720px;
    margin: 0 auto;
    width: 100%;
  }
`;

const BackRow = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
`;

const PageTitleWrap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs};
`;

const PageDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
`;

const NoticeBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: #FEF3C7;
  border: 1px solid #FDE68A;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 11px;
  color: #92400E;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const NoticeBoxLong = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.base};
  background: ${({ theme }) => theme.colors.primary}10;
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.primary};

  > svg { flex-shrink: 0; margin-top: 2px; }
`;

const NoticeContent = styled.div`
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};

  strong {
    color: ${({ theme }) => theme.colors.primary};
    display: block;
    margin-bottom: 4px;
  }
`;

const NoticeBoxSmall = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const BigAmountCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const BigAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  background: #FEF3C7;
  color: #D97706;
  border-radius: ${({ theme }) => theme.radius.full};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CardHeader = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

const ItemLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const ItemAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

const Field = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.bgWhite};
  outline: none;

  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
  &[readonly] { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const InputWrap = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  color: ${({ theme }) => theme.colors.textLight};
`;

const InputWithIcon = styled(Input)`
  padding-left: 40px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  margin-top: ${({ theme }) => theme.spacing.sm};

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
